class ProductModalGallery extends HTMLElement {
  connectedCallback() {
    onLibReady(['vue', 'swiper'], this.#init.bind(this));
  }

  #init() {
    new Vue({
      el: this,
      data: {
        show: false,
        title: '',
        activeImageId: '',
        images: [],
        primaryAction: null,
        loading: false,
      },
      template: `
        <div class="product-modal-gallery" v-if="show" @click="handleClickModal">
          <div class="product-modal-gallery__close-wrap">
            <div class="product-modal-gallery__close">
              <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" class="icon icon-close" fill="none" viewBox="0 0 18 17">
                <path d="M.865 15.978a.5.5 0 00.707.707l7.433-7.431 7.579 7.282a.501.501 0 00.846-.37.5.5 0 00-.153-.351L9.712 8.546l7.417-7.416a.5.5 0 10-.707-.708L8.991 7.853 1.413.573a.5.5 0 10-.693.72l7.563 7.268-7.418 7.417z" fill="currentColor">
              </svg>
            </div>
          </div>  
          
          <div class="product-modal-gallery__inner">
            <div class="product-modal-gallery__title" v-html="title" />
        
            <div class="product-modal-gallery__slider">
              <div class="swiper-container" ref="slider">
                <div class="swiper-wrapper">
                  <div class="swiper-slide" v-for="(image, index) in images">
                    <iframe :src="image.videoSrc" v-if="image.videoSrc" ref="video"></iframe>
                    
                    <img 
                      v-else
                      :srcset="image.src"
                      :alt="image.alt"
                      src="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"
                      width="520"
                      height="520"
                    >
                  </div>
                </div>
              </div>
        
              <div class="slider-button-prev swiper-button-lock" ref="sliderBtnPrev" v-html="Shopify.icons.arrowPrev" />
              <div class="slider-button-next swiper-button-lock" ref="sliderBtnNext" v-html="Shopify.icons.arrowNext" />
            </div>
        
            <div class="product-modal-gallery__thumbnail" v-if="images.length > 1">
              <div class="product-modal-gallery__thumbnail-list">                
                <div class="product-modal-gallery__image" v-for="(image, index) in images">
                  <template v-if="image.videoSrc">
                    <svg class="svg-icon" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px"
                      \t viewBox="0 0 496.158 496.158" style="enable-background:new 0 0 496.158 496.158;" xml:space="preserve">
                      \t<path style="fill:#ffa500;" d="M496.158,248.085c0-137.021-111.07-248.082-248.076-248.082C111.07,0.002,0,111.062,0,248.085
                      \tc0,137.002,111.07,248.071,248.083,248.071C385.088,496.155,496.158,385.086,496.158,248.085z"/>
                      \t<path style="fill:#FFFFFF;" d="M370.805,235.242L195.856,127.818c-4.776-2.934-11.061-3.061-15.951-0.322
                      \tc-4.979,2.785-8.071,8.059-8.071,13.762v214c0,5.693,3.083,10.963,8.046,13.752c2.353,1.32,5.024,2.02,7.725,2.02
                      \tc2.897,0,5.734-0.797,8.205-2.303l174.947-106.576c4.657-2.836,7.556-7.986,7.565-13.44
                      \tC378.332,243.258,375.452,238.096,370.805,235.242z"/>
                    </svg>
                  </template>
                    
                  <img 
                    :srcset="image.thumbnailSrc || image.src" 
                    :alt="image.alt"
                    ref="thumbnail"
                    src="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"
                    width="100"
                    height="100"
                    loading="lazy"
                    @click="handleSlideTo(index)"
                    :class="{ active: image.active }"
                  >
                </div>
              </div>
            </div>
            
            <div class="product-modal-gallery__primary-action" v-if="primaryAction">
              <button 
                class="button button--orange" 
                @click.prevent="handlePrimaryAction" 
                :class="{ 'loading': loading }"
                aria-label="Open gallery"
              >
                {{primaryAction.content}}
              </button>
            </div>
          </div>
        </div>
      `,

      mounted() {
        window.productGalleryOpen = this.open;
      },

      methods: {
        handleClickModal(evt) {
          if (!evt.target.closest('.product-modal-gallery__inner')) {
            this.close();
          }
        },

        open({images, title, primaryAction}) {
          this.show = true;
          this.images = images;
          this.title = title;
          this.primaryAction = primaryAction;

          const initialSlide = images.findIndex(image => image.active);
          setTimeout(this.initSlider.bind(this, initialSlide));
        },

        close() {
          this.show = false;
          this.loading = false;
          this.slider?.destroy();
        },

        async handlePrimaryAction() {
          if (this.loading || !this.primaryAction?.onAction) return;
          
          try {
            this.loading = true;
            const activeImage = this.images.find(img => img.active);
            const id = activeImage?.id;
            await this.primaryAction.onAction(id);
          } catch (error) {
            console.error('Primary action error:', error);
          } finally {
            this.loading = false;
          }
        },

        initSlider(initialSlide) {
          this.slider = new Swiper(this.$refs.slider, {
            initialSlide,
            speed: 500,
            slidesPerView: 1,
            observer: true,
            watchOverflow: true,
            navigation: {
              nextEl: this.$refs.sliderBtnNext,
              prevEl: this.$refs.sliderBtnPrev,
            },
            on: {
              slideChange: evt => {
                this.changeActiveImage(evt.activeIndex);
                this.stopVideo();
              },
            },
          });
        },

        handleSlideTo(index) {
          this.slider?.slideTo(index);
        },

        changeActiveImage(index) {
          this.images.forEach((image, idx) => {
            image.active = index === idx;
          });
          
          const activeImage = this.images[index];
          if (activeImage?.title) {
            this.title = activeImage.title;
          }
        },

        stopVideo() {
          if (this.$refs.video) {
            this.$refs.video.forEach(el => {
              el.contentWindow.postMessage('{"event":"command","func":"stopVideo","args":""}', '*');
            });
          }
        },
      },
    });
  }
}

customElements.define('product-modal-gallery', ProductModalGallery);
